/* eslint-disable no-redeclare */
import { useCallback, useRef } from "react";
import { cn } from "~/lib/utils";
import { PillSelectorContext } from "./context";

interface BaseProps {
  label: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface SingleSelectProps<T extends string> extends BaseProps {
  value: T | null;
  onChange: (value: T | null) => void;
  multi?: false;
}

interface MultiSelectProps<T extends string> extends BaseProps {
  value: T[] | null;
  onChange: (value: T[] | null) => void;
  multi: true;
}

type RootProps<T extends string> = SingleSelectProps<T> | MultiSelectProps<T>;

export function Root<T extends string>(props: SingleSelectProps<T>): React.ReactElement;
export function Root<T extends string>(props: MultiSelectProps<T>): React.ReactElement;
export function Root<T extends string>({
  value,
  onChange,
  multi,
  label,
  disabled,
  className,
  children,
}: RootProps<T>): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(
    (optionValue: string) => {
      if (multi) {
        const currentValues = (value as T[] | null) ?? [];
        const newValues = currentValues.includes(optionValue as T)
          ? currentValues.filter((v) => v !== optionValue)
          : [...currentValues, optionValue as T];
        (onChange as (v: T[] | null) => void)(newValues.length > 0 ? newValues : null);
      } else {
        const currentValue = value as T | null;
        (onChange as (v: T | null) => void)(currentValue === optionValue ? null : (optionValue as T));
      }
    },
    [value, onChange, multi],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, optionValue: string) => {
      const selector = multi ? "button" : '[role="radio"]';
      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>(selector);
      if (!buttons || buttons.length === 0) return;

      const values = Array.from(buttons).map((btn) => btn.dataset.value ?? "");
      const currentIndex = values.indexOf(optionValue);
      if (currentIndex === -1) return;

      let nextIndex: number | null = null;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % buttons.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      }

      if (nextIndex !== null) {
        buttons[nextIndex].focus();
        if (!multi) {
          const nextValue = values[nextIndex];
          handleToggle(nextValue);
        }
      }
    },
    [multi, handleToggle],
  );

  const contextValue = {
    value,
    multi,
    onToggle: handleToggle,
    disabled,
    containerRef,
    onKeyDown: handleKeyDown,
  };

  return (
    <PillSelectorContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={cn("flex flex-wrap gap-2", className)}
        role={multi ? "group" : "radiogroup"}
        aria-label={label}
      >
        {children}
      </div>
    </PillSelectorContext.Provider>
  );
}
